import customTargetingClient from './customTargetingClient';
import {
    CreateKeyInput,
    CreateValueInput,
    CustomTargetingKey,
    CustomTargetingValue,
    KeyCapacityResponse,
    MessageResponse,
    PagedValuesResponse,
    ValuesQuery
} from "@/types/custom-targeting";

// ==================== CUSTOM TARGETING KEYS ====================

export const getCustomTargetingKeys = async (networkCode?: string): Promise<CustomTargetingKey[]> => {
    const params = networkCode ? `?network_code=${networkCode}` : '';
    const response = await customTargetingClient.get(`/api/v1/custom-targeting/keys${params}`);
    return response.data.data;
};

export const getCustomTargetingKey = async (keyId: string): Promise<CustomTargetingKey> => {
    const response = await customTargetingClient.get(`/api/v1/custom-targeting/keys/${keyId}`);
    return response.data;
};

export const createCustomTargetingKey = async (data: CreateKeyInput): Promise<CustomTargetingKey> => {
    const response = await customTargetingClient.post('/api/v1/custom-targeting/keys', data);
    return response.data;
};

/**
 * Quanto do limite de values do GAM (~100 mil por key) cada key de UTM já usou.
 *
 * O total vem do próprio GAM e inclui os inativos, que continuam ocupando vaga.
 * O servidor guarda em cache por 15 min; `refresh` força nova consulta.
 */
export const getKeysCapacity = async (refresh = false): Promise<KeyCapacityResponse> => {
    const response = await customTargetingClient.get(
        `/api/v1/custom-targeting/keys-capacity${refresh ? '?refresh=true' : ''}`
    );
    return response.data;
};

// ==================== CUSTOM TARGETING VALUES ====================

export const getCustomTargetingValues = async (
    keyId?: string,
    statusFilter?: string
): Promise<CustomTargetingValue[]> => {
    const params = new URLSearchParams();
    if (keyId) params.append('key_id', keyId);
    if (statusFilter) params.append('status_filter', statusFilter);

    const queryString = params.toString();
    const response = await customTargetingClient.get(`/api/v1/custom-targeting/values${queryString ? `?${queryString}` : ''}`);
    return response.data.data;
};

/**
 * Busca uma página de values, com status e período já filtrados no servidor.
 *
 * A tela usava getCustomTargetingValues e filtrava em memória. Isso só
 * funcionava porque ninguém tinha aberto uma key grande: utm_term_hour tem
 * mais de 800 mil values, e baixar todos derrubava o worker da API.
 */
export const getPagedCustomTargetingValues = async (
    query: ValuesQuery
): Promise<PagedValuesResponse> => {
    const params = new URLSearchParams();
    params.append('key_id', query.keyId);
    params.append('page', String(query.page));
    params.append('per_page', String(query.perPage));
    if (query.status) params.append('status_filter', query.status);
    if (query.dateFrom) params.append('date_from', query.dateFrom);
    if (query.dateTo) params.append('date_to', query.dateTo);

    const response = await customTargetingClient.get(
        `/api/v1/custom-targeting/values?${params.toString()}`
    );
    return response.data;
};

export const getCustomTargetingValue = async (valueId: string): Promise<CustomTargetingValue> => {
    const response = await customTargetingClient.get(`/api/v1/custom-targeting/values/${valueId}`);
    return response.data;
};

export const createCustomTargetingValue = async (data: CreateValueInput): Promise<CustomTargetingValue> => {
    const response = await customTargetingClient.post('/api/v1/custom-targeting/values', data);
    return response.data;
};

export const deleteCustomTargetingValue = async (valueId: string): Promise<MessageResponse> => {
    const response = await customTargetingClient.delete(`/api/v1/custom-targeting/values/${valueId}`);
    return response.data;
};